import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const contactInfo = [
  {
    icon: Phone,
    title: 'Jyotsna Arora',
    value: '0468 883 356',
    link: 'tel:0468883356'
  },
  {
    icon: Phone,
    title: 'Nidhi Saini',
    value: '0452 510 447',
    link: 'tel:0452510447'
  },
  {
    icon: Mail,
    title: 'Jyotsna',
    value: 'Jyotsna@loansandbeyond.com',
    link: 'mailto:Jyotsna@loansandbeyond.com'
  },
  {
    icon: Mail,
    title: 'Nidhi',
    value: 'Nidhi@loansandbeyond.com',
    link: 'mailto:Nidhi@loansandbeyond.com'
  }
];

const serviceOptions = [
  { value: 'home-loan', label: 'Home Loan' },
  { value: 'refinancing', label: 'Refinancing' },
  { value: 'investment-loan', label: 'Investment Property Loan' },
  { value: 'first-home', label: 'First Home Buyer' },
  { value: 'commercial', label: 'Commercial Loan' },
  { value: 'other', label: 'Other / Not Sure' }
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-[#374151]/10 text-[#374151] rounded-full text-sm font-medium mb-6">
              Get In Touch
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-[#374151] leading-tight mb-6">
              Let's Find Your Perfect Home Loan
            </h1>
            <p className="text-lg md:text-xl text-gray-500 leading-relaxed">
              Ready to take the next step? Get in touch for a free, no-obligation consultation with one of our mortgage experts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white -mt-12 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                  <info.icon className="w-6 h-6 text-[#6B7280]" />
                </div>
                <h3 className="font-heading font-semibold text-[#374151] mb-2">{info.title}</h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    className="text-gray-500 hover:text-[#6B7280] transition-colors text-sm"
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-gray-500 text-sm">{info.value}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-32 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#374151] mb-2">
                Request a Callback
              </h2>
              <p className="text-gray-500 mb-8">
                Fill in the form below and one of our mortgage experts will be in touch within 24 hours.
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-2xl p-12 text-center border border-border/50"
                  data-testid="form-success"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-2xl text-[#374151] mb-3">
                    Thank You!
                  </h3>
                  <p className="text-gray-500 mb-6">
                    We've received your enquiry and one of our mortgage experts will be in touch within 24 hours.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-2 border-[#6B7280] text-[#6B7280] hover:bg-[#6B7280] hover:text-white rounded-full"
                  >
                    Submit Another Enquiry
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#374151] font-medium">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="h-12 rounded-lg bg-white border-border focus:ring-[#374151]"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#374151] font-medium">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="h-12 rounded-lg bg-white border-border focus:ring-[#374151]"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#374151] font-medium">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="0400 000 000"
                        className="h-12 rounded-lg bg-white border-border focus:ring-[#374151]"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-[#374151] font-medium">Service Required *</Label>
                      <Select onValueChange={handleServiceChange} value={formData.service_type}>
                        <SelectTrigger 
                          className="h-12 rounded-lg bg-white border-border"
                          data-testid="select-service"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#374151] font-medium">Your Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your situation and what you're looking for..."
                      rows={5}
                      className="rounded-lg bg-white border-border focus:ring-[#374151] resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full py-6 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Enquiry
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    By submitting this form, you agree to our privacy policy. We'll never share your information.
                  </p>
                </form>
              )}
            </motion.div>

            {/* Map / Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-[#6B7280] rounded-2xl p-8 text-white">
                <h3 className="font-heading font-semibold text-xl mb-6">Contact Our Brokers</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Jyotsna Arora</p>
                      <a href="tel:0468883356" className="text-white/80 hover:text-white transition-colors">
                        0468 883 356
                      </a>
                      <br />
                      <a href="mailto:Jyotsna@loansandbeyond.com" className="text-white/80 hover:text-white transition-colors text-sm">
                        Jyotsna@loansandbeyond.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Nidhi Saini</p>
                      <a href="tel:0452510447" className="text-white/80 hover:text-white transition-colors">
                        0452 510 447
                      </a>
                      <br />
                      <a href="mailto:Nidhi@loansandbeyond.com" className="text-white/80 hover:text-white transition-colors text-sm">
                        Nidhi@loansandbeyond.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-xl text-[#374151] mb-4">What Happens Next?</h3>
                <ol className="space-y-4">
                  <li className="flex gap-4">
                    <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 text-[#6B7280] font-semibold">1</span>
                    <div>
                      <p className="font-medium text-[#374151]">We'll Review Your Enquiry</p>
                      <p className="text-sm text-gray-500">One of our experts will review your situation and prepare options.</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 text-[#6B7280] font-semibold">2</span>
                    <div>
                      <p className="font-medium text-[#374151]">We'll Call You Back</p>
                      <p className="text-sm text-gray-500">Within 24 hours, we'll be in touch to discuss your needs.</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 text-[#6B7280] font-semibold">3</span>
                    <div>
                      <p className="font-medium text-[#374151]">Free Consultation</p>
                      <p className="text-sm text-gray-500">We'll provide tailored recommendations at no cost to you.</p>
                    </div>
                  </li>
                </ol>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
